<template>
  <div class="col-md-8 hos-summary-card-col">
    <div class="hos-summary-card">
      <div class="hos-summary-header d-flex justify-content-between align-items-start flex-wrap gap-2">
        <span class="hos-summary-title">{{ cardName }}</span>

        <div class="hos-summary-controls d-flex align-items-center flex-wrap gap-2">
          <select
            class="form-select form-select-sm hos-summary-select"
            v-model="activeGroupBy"
            @change.stop="onGroupByChange"
          >
            <option v-for="option in groupByOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>

          <select
            class="form-select form-select-sm hos-summary-select"
            v-model="statisticsItem[itemKey].period_type"
            @change.stop="onPeriodChange"
          >
            <option v-for="range in timeRangeOptions" :key="range.value" :value="range.value">
              {{ range.name }}
            </option>
          </select>

          <span class="hos-summary-see-all cursor-pointer" @click="navigateToDetail">See Details</span>
        </div>
      </div>

      <div class="hos-tab-track">
        <button
          v-for="type in productTypeTabs"
          :key="type.value"
          class="hos-tab"
          :class="{ 'hos-tab-active': activeProductType === type.value }"
          @click="changeProductType(type.value)"
        >
          {{ type.label }}
        </button>
      </div>

      <ProductionTargetActualTable
        v-if="activeRows"
        :rows="activeRows"
        :group-by="activeGroupBy"
      />
      <div v-else class="hos-summary-empty">
        Please refresh the dashboard to load summary data.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ProductionTargetActualTable from "@/components/molecule/company/dashboard/dynamic-dashboard/production-target-actual-table/ProductionTargetActualTable";

const emit = defineEmits(['onTypeChange']);

const props = defineProps({
  statisticsItem: Object,
  itemKey: String,
  page: Object,
  timeRange: Array,
  productTypeOptions: Array,
  groupByOptions: Array,
});

const route = useRoute();
const router = useRouter();

const cardData = computed(() => {
  const item = props.statisticsItem[props.itemKey];
  if (! item) return {};
  if (! item.data) return {};
  return item.data;
});

const cardName = computed(() => cardData.value.name || 'Production Target Actual');

const productTypeTabs = computed(() => props.productTypeOptions || []);

const activeProductType = ref('');
const activeGroupBy = ref(cardData.value.group_by || 'category');

watch(productTypeTabs, (tabs) => {
  if (! tabs.length) return
  if (activeProductType.value) return

  activeProductType.value = tabs[0].value;
}, { immediate: true });

const timeRangeOptions = computed(() => {
  return cardData.value.time_ranges || props.timeRange;
});

const activeRows = computed(() => {
  return cardData.value[activeProductType.value];
});

const emitChange = (groupBy) => {
  const item = props.statisticsItem[props.itemKey];
  emit('onTypeChange', item, groupBy);
};

const changeProductType = (value) => {
  activeProductType.value = value;
};

const onGroupByChange = () => {
  emitChange(activeGroupBy.value);
};

const onPeriodChange = () => {
  emitChange(activeGroupBy.value);
};

const navigateToDetail = () => {
  const item = props.statisticsItem[props.itemKey];
  const data = cardData.value;
  const params = Object.assign({}, route.params, { pageId: data.page_id });
  const query = {
    start: item.period_start,
    end: item.period_end,
    group_by: activeGroupBy.value,
    product_type: activeProductType.value
  };

  router.push({ name: data.url, params, query });
};
</script>

<style scoped>
.hos-summary-card-col {
  padding: 8px;
}

.hos-summary-card {
  background-color: #ffffff;
  border: 1px solid #e8e9ec;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.06);
  padding: 20px;
}

.hos-summary-header {
  padding-bottom: 14px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f0f1f4;
}

.hos-summary-title {
  font-size: 14px;
  font-weight: 700;
  color: #2b2b2b;
}

.hos-summary-see-all {
  font-size: 12px;
  font-weight: 600;
  color: #16a085;
  white-space: nowrap;
}

.hos-summary-controls {
  flex: 1 1 auto;
  justify-content: flex-end;
}

.hos-summary-select {
  width: auto;
  min-width: 110px;
}

@media (max-width: 576px) {
  .hos-summary-header {
    flex-direction: column;
    align-items: stretch;
  }

  .hos-summary-controls {
    justify-content: flex-start;
  }

  .hos-summary-select {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.hos-tab-track {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  background-color: #f5f6f8;
  border-radius: 10px;
  padding: 5px;
  margin-bottom: 12px;
}

.hos-tab {
  flex: 1 1 auto;
  border: none;
  background: transparent;
  color: #6e6b7b;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.hos-tab:hover {
  color: #16a085;
}

.hos-tab-active {
  background-color: #ffffff;
  color: #16a085;
  box-shadow: 0 2px 6px rgba(34, 41, 47, 0.08);
}

.hos-summary-empty {
  text-align: center;
  color: #8a8a8a;
  font-size: 13px;
  padding: 40px 0;
}
</style>
